import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { useSnackbar } from 'notistack';
import tipoFormularioService, { TipoFormulario } from '../../services/tipoFormularioService';

const defaultForm: TipoFormulario = {
  nroTipoFormulario: '',
  descripcion: '',
  area: 'Ventas',
  utilizaLibroIva: 'N',
  sumaResta: 'S',
  stockNegativo: 0,
  infoCons: 0,
  descuentaStock: 0,
  nota: ''
};

const areas = ['Ventas', 'Compras', 'Tesorería', 'Inventario', 'Producción'];

const TipoFormularioForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroTipoFormulario } = useParams();
  const [formData, setFormData] = useState<TipoFormulario>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTipoFormulario = useCallback(async () => {
    if (!nroTipoFormulario || nroTipoFormulario === 'nuevo') return;
    try {
      setLoading(true);
      const tipo = await tipoFormularioService.getTipoFormulario(nroTipoFormulario);
      setFormData(tipo);
    } catch (error) {
      enqueueSnackbar('Error al cargar el tipo de formulario', { variant: 'error' });
      navigate('/sistema/tipo-formulario');
    } finally {
      setLoading(false);
    }
  }, [nroTipoFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroTipoFormulario && nroTipoFormulario !== 'nuevo') loadTipoFormulario();
  }, [nroTipoFormulario, loadTipoFormulario]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'stockNegativo' || name === 'infoCons' || name === 'descuentaStock') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nroTipoFormulario.trim() || !formData.descripcion.trim()) {
        enqueueSnackbar('Complete los campos obligatorios', { variant: 'warning' });
        return;
      }

      if (nroTipoFormulario && nroTipoFormulario !== 'nuevo') {
        await tipoFormularioService.updateTipoFormulario(nroTipoFormulario, formData);
      } else {
        await tipoFormularioService.createTipoFormulario(formData);
      }

      enqueueSnackbar('Tipo de formulario guardado exitosamente', { variant: 'success' });
      navigate('/sistema/tipo-formulario');
    } catch (error: any) {
      console.error('Error al guardar tipo de formulario:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar el tipo de formulario', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {nroTipoFormulario && nroTipoFormulario !== 'nuevo' ? 'Editar Tipo de Formulario' : 'Nuevo Tipo de Formulario'}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                  <TextField 
                    name="nroTipoFormulario" 
                    label="N° Tipo" 
                    value={formData.nroTipoFormulario} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!(nroTipoFormulario && nroTipoFormulario !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField 
                  name="descripcion" 
                  label="Descripción" 
                  value={formData.descripcion} 
                  onChange={handleChange} 
                  fullWidth 
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Área</InputLabel>
                  <Select
                    name="area"
                    value={formData.area}
                    onChange={(e) => setFormData(prev => ({ ...prev, area: e.target.value }))}
                    label="Área"
                    sx={{ borderRadius: 2 }}
                  >
                    {areas.map(area => (
                      <MenuItem key={area} value={area}>{area}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Utiliza Libro IVA</InputLabel>
                  <Select
                    name="utilizaLibroIva"
                    value={formData.utilizaLibroIva}
                    onChange={(e) => setFormData(prev => ({ ...prev, utilizaLibroIva: e.target.value }))}
                    label="Utiliza Libro IVA"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="S">Sí</MenuItem>
                    <MenuItem value="N">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Suma/Resta</InputLabel>
                  <Select
                    name="sumaResta"
                    value={formData.sumaResta}
                    onChange={(e) => setFormData(prev => ({ ...prev, sumaResta: e.target.value }))}
                    label="Suma/Resta"
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="S">Suma</MenuItem>
                    <MenuItem value="R">Resta</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  name="nota" 
                  label="Nota" 
                  value={formData.nota || ''} 
                  onChange={handleChange} 
                  fullWidth 
                  multiline
                  rows={3}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/sistema/tipo-formulario')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TipoFormularioForm;
